<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <div class="stage-page-header">
            <div class="stage-page-header-row">
                <div class="stage-page-header-main">
                    <h1 class="stage-page-title">{{ $t('Modul-Bibliothek') }}</h1>
                </div>
                <div class="stage-page-header-actions">
                    <router-link class="btn-stage-icon-plain" :title="$t('Back')" :aria-label="$t('Back')" to="/workspace/cms">
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.arrow-left"></svg-vue>
                    </router-link>
                    <router-link class="btn-stage-icon-plain" :title="$t('Modul anlegen')" :aria-label="$t('Modul anlegen')" to="/workspace/cms/module-library/new">
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.plus"></svg-vue>
                    </router-link>
                </div>
            </div>
        </div>

        <div class="stage-page-body stage-page-fill">
            <div class="w-full h-full bg-white shadow sm:rounded relative overflow-hidden flex flex-col min-h-0">
                <loading :status="loading"/>

                <div class="relative flex-1 min-h-0 overflow-hidden">
                    <div class="h-full overflow-auto">
                        <div v-if="modules.length > 0" class="hidden sm:block">
                            <div class="align-middle inline-block min-w-full border-b border-ultralight">
                                <table class="table-listview table-listview--zebra">
                                    <thead>
                                    <tr>
                                        <th class="table-listview-l-th">{{ $t('Modul') }}</th>
                                        <th class="table-listview-l-th whitespace-nowrap w-48">{{ $t('Preset') }}</th>
                                        <th class="table-listview-l-th whitespace-nowrap w-40">{{ $t('Verwendung') }}</th>
                                        <th class="table-listview-l-th whitespace-nowrap w-32">{{ $t('Einbindungen') }}</th>
                                        <th class="table-listview-l-th whitespace-nowrap w-48">{{ $t('Erstellt von') }}</th>
                                        <th class="table-listview-l-th whitespace-nowrap w-48">{{ $t('Letzte Veröffentlichung') }}</th>
                                        <th class="table-listview-l-th whitespace-nowrap w-40">{{ $t('Update') }}</th>
                                    </tr>
                                    </thead>
                                    <tbody class="bg-white">
                                    <tr
                                        v-for="module in modules"
                                        :key="module.id"
                                        class="cursor-pointer"
                                        @click="$router.push('/workspace/cms/module-library/' + module.id + '/edit')"
                                    >
                                        <td class="table-listview-l-td whitespace-nowrap">
                                            <div class="flex items-center min-w-0">
                                                <div class="mr-3 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gray-700">
                                                    <svg-vue class="w-8 h-8 p-1.5 text-gray-200" icon="font-awesome.regular.cubes"></svg-vue>
                                                </div>
                                                <div class="min-w-0">
                                                    <div class="flex items-center gap-2 min-w-0">
                                                        <div class="text-sm font-medium text-gray-900 truncate">{{ module.name }}</div>
                                                        <span :class="moduleStatusMeta(module).dotClass" class="h-2 w-2 rounded-full flex-shrink-0"></span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div v-if="module.has_unpublished_changes" class="text-xs text-amber-600 mt-1">
                                                {{ $t('Unveröffentlichte Änderungen') }}
                                            </div>
                                            <div v-else-if="!module.published_at" class="text-xs text-gray-500 mt-1">
                                                {{ $t('Noch nicht veröffentlicht') }}
                                            </div>
                                        </td>
                                        <td class="table-listview-l-td whitespace-nowrap text-sm text-gray-700">
                                            {{ module.module_type ? module.module_type.name : '-' }}
                                        </td>
                                        <td class="table-listview-l-td whitespace-nowrap text-sm text-gray-700">
                                            {{ module.is_global ? $t('Globale Referenz') : $t('Instanz') }}
                                        </td>
                                        <td class="table-listview-l-td whitespace-nowrap text-sm text-gray-700">
                                            {{ module.placements_count }}
                                        </td>
                                        <td class="table-listview-l-td whitespace-nowrap">
                                            <div class="flex items-center min-w-0 max-w-[12rem] text-sm text-gray-500">
                                                <img
                                                    v-if="createdActorAvatar(module)"
                                                    :src="createdActorAvatar(module)"
                                                    :alt="createdActorName(module)"
                                                    :title="createdActorName(module)"
                                                    class="h-6 w-6 rounded-full border border-white object-cover mr-2 flex-shrink-0"
                                                >
                                                <span class="truncate text-gray-900">{{ createdActorName(module) }}</span>
                                            </div>
                                        </td>
                                        <td class="table-listview-l-td whitespace-nowrap">
                                            <div v-if="module.published_by_user" class="flex items-center min-w-0 max-w-[12rem] text-sm text-gray-500">
                                                <img
                                                    :src="publishedActorAvatar(module)"
                                                    :alt="publishedActorName(module)"
                                                    :title="publishedActorName(module)"
                                                    class="h-6 w-6 rounded-full border border-white object-cover mr-2 flex-shrink-0"
                                                >
                                                <span class="truncate text-gray-900">{{ publishedActorName(module) }}</span>
                                            </div>
                                            <div v-else class="text-sm text-gray-500">-</div>
                                        </td>
                                        <td class="table-listview-l-td whitespace-nowrap text-sm text-gray-500">
                                            {{ module.updated_at ? $momentFormatDateTimeAgo(module.updated_at) : '-' }}
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div v-else-if="!loading" class="h-full">
                            <empty-state />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {
    cmsCreatedActorAvatar,
    cmsCreatedActorName,
    cmsPublishedActorAvatar,
    cmsPublishedActorName,
} from '@/utils/cms-update-meta';
import {cmsPublicationStatusMeta} from '@/utils/cms-status-badge';

export default {
    name: 'cms-module-library',
    data() {
        return {
            loading: true,
            modules: [],
        };
    },
    mounted() {
        this.loadModules();
    },
    methods: {
        createdActorAvatar(module) {
            return cmsCreatedActorAvatar(module);
        },
        createdActorName(module) {
            return cmsCreatedActorName(module);
        },
        publishedActorAvatar(module) {
            return cmsPublishedActorAvatar(module);
        },
        publishedActorName(module) {
            return cmsPublishedActorName(module);
        },
        moduleStatusMeta(module) {
            return cmsPublicationStatusMeta(this, module);
        },
        loadModules() {
            this.loading = true;
            axios.get('api/workspace/admin/cms/modules').then((response) => {
                this.modules = Array.isArray(response.data) ? response.data : [];
                this.loading = false;
            }).catch(() => {
                this.loading = false;
            });
        },
    },
}
</script>
